import { memo, useState, useCallback, useEffect } from 'react'
import type { Advertiser, AdCampaign } from '../../store/slices/adsSlice.js'
import { campaignsApi, advertisersApi } from '../../lib/adsApi.js'
import { ModalShell } from '../common/ModalShell.js'

// ─── Props ────────────────────────────────────────────────────────────────────

interface CampaignModalProps {
  channelId:   string
  advertisers: Advertiser[]
  /** Si fourni, le modal est en mode édition. */
  campaign?:   AdCampaign | null
  onCreated?:  (c: AdCampaign) => void
  onUpdated?:  (c: AdCampaign) => void
  onClose:     () => void
}

// ─── Créneaux horaires prédéfinis ─────────────────────────────────────────────

const SLOT_PRESETS = [
  'Matin (06h-12h)', 'Midi (12h-14h)', 'Après-midi (14h-18h)',
  'Soir (18h-22h)', 'Prime time (20h-22h)', 'Nuit (22h-06h)',
]

// ─── Helpers ──────────────────────────────────────────────────────────────────

function isoToInputDate(iso: string | null | undefined): string {
  if (!iso) return ''
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return ''
  return d.toISOString().slice(0, 10)
}

// ─── Composant ────────────────────────────────────────────────────────────────

/**
 * Modal unifié de création / édition d'une campagne publicitaire.
 *
 *   - Mode création : `campaign` non fourni → bouton « Créer la campagne »
 *   - Mode édition  : `campaign` fourni     → bouton « Enregistrer »
 *
 * Le mode édition prérenseigne tous les champs et appelle
 * `campaignsApi.update()` au save (sans permettre de changer le canal).
 *
 * Conserve l'ancien nom `NewCampaignModal` pour rester compatible avec
 * l'import existant dans `AdsView.tsx`.
 */
export const NewCampaignModal = memo(function NewCampaignModal({
  channelId, advertisers, campaign, onCreated, onUpdated, onClose,
}: CampaignModalProps) {
  const isEdit = !!campaign

  const [visible,   setVisible]   = useState(false)
  const [saving,    setSaving]    = useState(false)
  const [error,     setError]     = useState<string | null>(null)
  const [newAdvMode, setNewAdvMode] = useState(false)

  const [name,         setName]         = useState(campaign?.name ?? '')
  const [advertiserId, setAdvertiserId] = useState(campaign?.advertiserId ?? advertisers[0]?.id ?? '')
  const [newAdvName,   setNewAdvName]   = useState('')
  const [spotsTotal,   setSpotsTotal]   = useState(String(campaign?.spotsTotal ?? 10))
  const [maxDailyFrequency,  setMaxDailyFrequency]  = useState(campaign?.maxDailyFrequency  != null ? String(campaign.maxDailyFrequency)  : '')
  const [maxWeeklyFrequency, setMaxWeeklyFrequency] = useState(campaign?.maxWeeklyFrequency != null ? String(campaign.maxWeeklyFrequency) : '')
  const [startDate, setStartDate] = useState(() => campaign ? isoToInputDate(campaign.startDate) : new Date().toISOString().slice(0, 10))
  const [endDate,   setEndDate]   = useState(() => {
    if (campaign) return isoToInputDate(campaign.endDate)
    const d = new Date(); d.setMonth(d.getMonth() + 1); return d.toISOString().slice(0, 10)
  })
  const [selectedSlots, setSelectedSlots] = useState<string[]>(campaign?.targetSlots ?? ['Soir (18h-22h)'])

  useEffect(() => { const t = setTimeout(() => setVisible(true), 10); return () => clearTimeout(t) }, [])
  const handleClose = useCallback(() => {
    setVisible(false)
    setTimeout(onClose, 200)
  }, [onClose])

  const toggleSlot = useCallback((slot: string) => {
    setSelectedSlots(prev => prev.includes(slot) ? prev.filter(s => s !== slot) : [...prev, slot])
  }, [])

  const handleSubmit = useCallback(async (e: React.FormEvent) => {
    e.preventDefault()
    setSaving(true); setError(null)
    try {
      let advId = advertiserId
      // Crée l'annonceur si mode nouveau
      if (newAdvMode && newAdvName.trim()) {
        const adv = await advertisersApi.create({ name: newAdvName.trim() })
        advId = adv.id
      }

      const payload = {
        name:         name.trim(),
        spotsTotal:   parseInt(spotsTotal),
        startDate:    new Date(startDate).toISOString(),
        endDate:      new Date(endDate).toISOString(),
        targetSlots:  selectedSlots,
        maxDailyFrequency:  maxDailyFrequency  ? parseInt(maxDailyFrequency)  : null,
        maxWeeklyFrequency: maxWeeklyFrequency ? parseInt(maxWeeklyFrequency) : null,
      }

      if (isEdit && campaign) {
        // Mode édition : on ne change pas channelId/advertiserId via PATCH (omis dans le schema backend)
        const updated = await campaignsApi.update(campaign.id, payload)
        onUpdated?.(updated)
      } else {
        const created = await campaignsApi.create({
          channelId,
          advertiserId: advId,
          ...payload,
          status: 'DRAFT',
        })
        onCreated?.(created)
      }
    } catch (err: any) {
      setError(err.message); setSaving(false)
    }
  }, [
    isEdit, campaign, channelId, advertiserId, newAdvMode, newAdvName, name,
    spotsTotal, startDate, endDate, selectedSlots, maxDailyFrequency,
    maxWeeklyFrequency, onCreated, onUpdated,
  ])

  const submitDisabled = saving || !name || (!isEdit && !advertiserId && !newAdvName)

  return (
    <ModalShell
      onClose={handleClose}
      title={isEdit ? 'Modifier la campagne' : 'Nouvelle campagne'}
      visible={visible}
    >
        <form onSubmit={handleSubmit} className="tp-modal-form">
          {error && <div className="tp-feedback tp-feedback--error" role="alert">{error}</div>}

          {/* Nom de la campagne */}
          <CampaignField label="Nom de la campagne *">
            <input value={name} onChange={e => setName(e.target.value)} required autoFocus placeholder="Campagne printemps 2025" className="tp-input" />
          </CampaignField>

          {/* Annonceur — uniquement à la création (figé en édition) */}
          {!isEdit ? (
            <div className="tp-form-field">
              <div className="ads-field-heading">
                <span className="tp-field-label">Annonceur *</span>
                <button type="button" onClick={() => setNewAdvMode(v => !v)} className="ads-field-link">
                  {newAdvMode ? '← Choisir existant' : '+ Nouvel annonceur'}
                </button>
              </div>
              {newAdvMode ? (
                <input value={newAdvName} onChange={e => setNewAdvName(e.target.value)} placeholder="Nom de l'annonceur" className="tp-input" required />
              ) : (
                <select value={advertiserId} onChange={e => setAdvertiserId(e.target.value)} className="tp-select" required>
                  <option value="">— Choisir —</option>
                  {advertisers.map(a => <option key={a.id} value={a.id}>{a.name}</option>)}
                </select>
              )}
            </div>
          ) : (
            <div className="tp-form-field">
              <span className="tp-field-label">Annonceur</span>
              <div className="ads-disabled-field" aria-disabled="true">
                {campaign?.advertiser?.name ?? advertisers.find(a => a.id === campaign?.advertiserId)?.name ?? '—'}
                <span className="ads-disabled-field__hint">(non modifiable)</span>
              </div>
            </div>
          )}

          {/* Dates + spots */}
          <div className="tp-form-grid tp-form-grid--three ads-campaign-dates">
            <CampaignField label="Début">
              <input type="date" value={startDate} onChange={e => setStartDate(e.target.value)} className="tp-input" required />
            </CampaignField>
            <CampaignField label="Fin">
              <input type="date" value={endDate} onChange={e => setEndDate(e.target.value)} className="tp-input" required />
            </CampaignField>
            <CampaignField label="Nb spots">
              <input type="number" value={spotsTotal} onChange={e => setSpotsTotal(e.target.value)} min="1" className="tp-input" required />
            </CampaignField>
          </div>

          {/* Créneaux cibles */}
          <fieldset className="tp-form-field">
            <legend className="tp-field-label">Créneaux cibles</legend>
            <div className="ads-slot-list">
              {SLOT_PRESETS.map(slot => (
                <button
                  key={slot}
                  type="button"
                  onClick={() => toggleSlot(slot)}
                  className={selectedSlots.includes(slot) ? 'ads-slot is-selected' : 'ads-slot'}
                  aria-pressed={selectedSlots.includes(slot)}
                >
                  {slot}
                </button>
              ))}
            </div>
          </fieldset>

          <div className="tp-form-grid">
            <CampaignField label="Freq. max / jour">
              <input type="number" value={maxDailyFrequency} onChange={e => setMaxDailyFrequency(e.target.value)} min="1" placeholder="Illimitée" className="tp-input" />
            </CampaignField>
            <CampaignField label="Freq. max / semaine">
              <input type="number" value={maxWeeklyFrequency} onChange={e => setMaxWeeklyFrequency(e.target.value)} min="1" placeholder="Illimitée" className="tp-input" />
            </CampaignField>
          </div>

          {/* Actions */}
          <div className="tp-modal-actions">
            <button type="button" onClick={handleClose} className="tp-button">Annuler</button>
            <button
              type="submit"
              disabled={submitDisabled}
              className="tp-button tp-button--primary"
              data-state={saving ? 'loading' : undefined}
            >
              {saving ? (isEdit ? 'Enregistrement…' : 'Création…') : isEdit ? 'Enregistrer' : 'Créer la campagne'}
            </button>
          </div>
        </form>
    </ModalShell>
  )
})

const CampaignField = memo(function CampaignField({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="tp-form-field">
      <span className="tp-field-label">{label}</span>
      {children}
    </label>
  )
})
